import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React from 'react'
import { PillarPage } from 'src/constants/searchPages'

interface IProps {
  /** When provided, the breadcrumb trail includes a final crumb for the city
   *  (and the on-demand crumb becomes a link). When omitted, the on-demand
   *  crumb is the current page. */
  cityDisplayName?: string
  /** Same-origin relative path (already validated by the parent via
   *  `isSafeReturnPath`) pointing back to the search-results page the user
   *  came from. When provided, the component renders a "← Volver a tus
   *  resultados" affordance above the breadcrumb trail. Undefined for direct/
   *  organic entries — the trail still renders normally. */
  searchReturnHref?: string
}

const PILLAR_HREF = `/${PillarPage.SelfStorage}`
const ON_DEMAND_HREF = `/${PillarPage.SelfStorage}/almacenamiento-a-domicilio`

/**
 * Visual breadcrumb trail rendered inline at the top of the hero section.
 *
 * Two trail variants:
 *   Parent page: Inicio > Mini Bodegas > Almacenamiento a Domicilio
 *   City page:   Inicio > Mini Bodegas > Almacenamiento a Domicilio > {city}
 *
 * The current page is rendered as a non-link `<span>` with `aria-current="page"`;
 * earlier crumbs are anchors. Wrapping element is `<nav aria-label="Breadcrumb">`
 * with an `<ol>` per WAI-ARIA breadcrumb pattern.
 *
 * When `searchReturnHref` is set (the user navigated here from a search-results
 * card that propagated `?searchReturn=…`), an additional row above the trail
 * shows "← Volver a tus resultados" linking back to the originating search
 * URL. Browser back works the same — this is a discoverable affordance, not
 * a substitute. Mobile renders it more prominently than desktop because the
 * mobile back button is harder to discover.
 *
 * Nav clearance and section background are owned by the wrapping hero — this
 * component renders no padding-top and no background. Its only consumer,
 * `HeroOnDemand`, is cream on mobile and ink (photo + scrim) from `md:`, so
 * every colour carries an `md:` light-on-dark override.
 */
const Breadcrumbs = ({ cityDisplayName, searchReturnHref }: IProps) => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')
  const isCityPage = Boolean(cityDisplayName)

  return (
    <div className="flex flex-col gap-3">
      {searchReturnHref && (
        <Link
          href={searchReturnHref}
          className="inline-flex w-fit items-center gap-1.5 text-[13px] font-medium text-blue-500 underline decoration-blue-500/40 underline-offset-4 transition-colors hover:text-blue-700 hover:decoration-blue-500 md:hover:text-white-500"
        >
          <ArrowBackRoundedIcon className="text-[16px]" />
          <span>{t('BREADCRUMBS.RETURN_TO_SEARCH')}</span>
        </Link>
      )}

      <nav aria-label="Breadcrumb">
        <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] leading-relaxed text-ink-200 md:text-white-500/70">
          <li>
            <Link
              href="/"
              className="transition-colors hover:text-blue-500 md:hover:text-white-500"
            >
              {t('BREADCRUMBS.HOME')}
            </Link>
          </li>
          <li
            aria-hidden="true"
            className="text-ink-100/60 md:text-white-500/40"
          >
            ›
          </li>
          <li>
            <Link
              href={PILLAR_HREF}
              className="transition-colors hover:text-blue-500 md:hover:text-white-500"
            >
              {t('BREADCRUMBS.PILLAR')}
            </Link>
          </li>
          <li
            aria-hidden="true"
            className="text-ink-100/60 md:text-white-500/40"
          >
            ›
          </li>
          <li>
            {isCityPage ? (
              <Link
                href={ON_DEMAND_HREF}
                className="transition-colors hover:text-blue-500 md:hover:text-white-500"
              >
                {t('BREADCRUMBS.ON_DEMAND')}
              </Link>
            ) : (
              <span
                aria-current="page"
                className="font-medium text-ink-700 md:text-white-500"
              >
                {t('BREADCRUMBS.ON_DEMAND')}
              </span>
            )}
          </li>
          {isCityPage && (
            <>
              <li
                aria-hidden="true"
                className="text-ink-100/60 md:text-white-500/40"
              >
                ›
              </li>
              <li>
                <span
                  aria-current="page"
                  className="font-medium text-ink-700 md:text-white-500"
                >
                  {cityDisplayName}
                </span>
              </li>
            </>
          )}
        </ol>
      </nav>
    </div>
  )
}

export default React.memo(Breadcrumbs)
